export const defaultAnswerSheet = {
    habilitada: false, rascunho: false, edital: ':nome_seletivo', cargo: ':nome_cargo',
    nome: ':nome_candidato', inscricao: ':inscricao_candidato', cpf: ':cpf_candidato',
    instrucoes: 'Confira seus dados e o número de cada questão. Preencha completamente uma única bolha por questão, com caneta esferográfica de tinta azul ou preta. Evite rasuras.'
};

export function replaceSheetTags(text, processo, user = null, nomeProva = '') {
    const values = {
        ':nome_seletivo': processo?.faculdade?.name ?? '', ':nome_cargo': processo?.name ?? '', ':nome_prova': nomeProva,
        ':nome_candidato': user?.name ?? '', ':inscricao_candidato': user?.numero_inscricao ?? '',
        ':cpf_candidato': user?.cpf ?? ''
    };
    return Object.entries(values).reduce((value, [tag, replacement]) => value.split(tag).join(String(replacement)), String(text ?? ''));
}

// Read the saved booklet, preserving its numbering and alternatives even when a question spans pages.
export function answerSheetQuestions(pages = []) {
    const questions = new Map();
    const seenIds = new Set();
    let current = null;
    const selector = '[id^="enunciado_"], [id^="alternativa_"], .questao, .alternativa';
    pages.forEach(page => {
        const doc = new DOMParser().parseFromString(page.value || '', 'text/html');
        doc.querySelectorAll(selector).forEach(element => {
            // Pagination can unwrap the original IDs and retain only these classes.
            if (element.parentElement?.closest(selector)) return;
            if (element.id && seenIds.has(element.id)) return;
            if (element.id) seenIds.add(element.id);
            const content = element.textContent.trim();
            const isAlternative = element.id.startsWith('alternativa_') || element.classList.contains('alternativa');
            if (!isAlternative) {
                const number = content.match(/^(\d+)\s*\)/);
                if (number) {
                    const index = Number(number[1]);
                    if (!questions.has(index)) questions.set(index, { number: index, answers: new Set() });
                    current = questions.get(index);
                } else if (element.id.startsWith('enunciado_')) {
                    current = null;
                }
            } else if (current) {
                const letter = content.match(/^\(?([A-Z])\)/);
                if (letter) current.answers.add(letter[1]);
                else if (element.id.startsWith('alternativa_')) current.answers.add(element.id);
            }
        });
    });
    return Array.from(questions.values()).filter(question => question.answers.size > 0)
        .map(question => ({ number: question.number, alternatives: question.answers.size }));
}


// Manual capacity prints additional positions; automatic mode keeps only the exam questions.
export function answerSheetDistribution(questions, map = {}) {
    if (!questions.length) return [];
    const alternatives = Math.max(...questions.map(question => question.alternatives));
    const items = questions.map(question => ({ ...question, alternatives }));
    const requested = Number(map.columns);
    const rows = Number(map.rows_per_column);
    const custom = map.mode === 'custom' && Number.isInteger(requested) && requested >= 1 && requested <= 3
        && Number.isInteger(rows) && rows >= 1 && rows <= 200 && requested * rows >= items.length;
    if (custom) {
        const lastNumber = Math.max(...items.map(question => question.number));
        const missing = requested * rows - items.length;
        for (let i = 1; i <= missing; i++) items.push({ number: lastNumber + i, alternatives });
        return [Array.from({ length: requested }, (_, column) => items.slice(column * rows, (column + 1) * rows))];
    }
    const count = Math.ceil(items.length / 75);
    const pages = [];
    let offset = 0;
    for (let page = 0; page < count; page++) {
        const size = Math.ceil((items.length - offset) / (count - page));
        const columns = Math.max(1, Math.min(3, Math.floor(size / 10)));
        const groups = [];
        let used = 0;
        for (let column = 0; column < columns; column++) {
            const length = Math.ceil((size - used) / (columns - column));
            groups.push(items.slice(offset + used, offset + used + length));
            used += length;
        }
        pages.push(groups);
        offset += size;
    }
    return pages;
}

export const defaultAnswerSheetElements = [
    { id: 'logo', type: 'logo', x: 36, y: 38, width: 112, height: 100 },
    { id: 'header', type: 'header', x: 168, y: 34, width: 573, height: 112 },
    { id: 'title', type: 'title', x: 36, y: 164, width: 705, height: 46 },
    { id: 'instructions', type: 'instructions', x: 36, y: 230, width: 705, height: 198 },
    { id: 'identity', type: 'identity', x: 36, y: 448, width: 705, height: 86 },
    { id: 'answers', type: 'answers', x: 36, y: 556, width: 705, height: 440 },
    { id: 'signature', type: 'signature', x: 36, y: 1032, width: 705, height: 54 },
];

export function answerSheetElements(settings) {
    const items = settings?.elements?.length ? settings.elements : defaultAnswerSheetElements;
    const previous = [["header",28,100],["title",140,38],["identity",192,116],["instructions",324,102],["answers",440,448],["barcode",948,70],["signature",1045,46]];
    const untouched = settings?.elements?.length && previous.every(([type, y, height]) => items.some(item => item.type === type && item.x === 36 && item.y === y && item.width === 705 && item.height === height && item.content == null));
    if (untouched) return [...defaultAnswerSheetElements.map(item => ({ ...item })), ...items.filter(item => !previous.some(([type]) => item.type === type)).map(item => ({ ...item }))];
    return items.map(item => ({ ...item }));
}

export const answerSheetTags = ':nome_seletivo, :nome_cargo, :nome_prova, :nome_candidato, :inscricao_candidato e :cpf_candidato';
const escapeHtml = value => String(value ?? '').replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));

export function answerSheetBlockContent(element, settings = defaultAnswerSheet) {
    if (element.content != null) return element.content;
    const field = name => escapeHtml(settings[name] ?? defaultAnswerSheet[name]);
    switch (element.type) {
        case 'logo': return '<p style="margin:0;text-align:center"><img src=":logo_seletivo" alt="Logo do seletivo" style="width:106px;height:88px;object-fit:contain"></p>';
        case 'header': return `<p style="margin:0 0 9px;font-size:10px;font-weight:bold;letter-spacing:1.3px;color:#526174">IDENTIFICAÇÃO DA AVALIAÇÃO</p><p style="font-size:21px;font-weight:bold;line-height:1.2;margin:0 0 9px">${field('edital')}</p><p style="font-size:15px;line-height:1.3;margin:0"><strong>Vaga / cargo:</strong> ${field('cargo')}</p>`;
        case 'title': return '<p style="font-size:24px;font-weight:bold;margin:0;padding:8px 0;border-top:2px solid #172334;border-bottom:1px solid #aeb8c5">:tipo_folha</p>';
        case 'instructions': return `<div style="border:1px solid #b9c4d0;border-radius:6px;padding:13px 17px;font-size:12px;line-height:1.5"><p style="font-size:14px;font-weight:bold;margin:0 0 7px">Instruções para preenchimento</p><ol style="padding-left:19px;margin:0"><li>${field('instrucoes')}</li><li>Confira seu nome, número de inscrição e a vaga / cargo antes de começar.</li><li>Marque somente uma alternativa por questão. Marcações duplas, incompletas ou rasuradas podem invalidar a resposta.</li><li>Não dobre, amasse ou faça anotações fora dos espaços destinados às respostas.</li><li>Assine no campo indicado e entregue a folha definitiva ao fiscal ao concluir a avaliação. O rascunho não será corrigido.</li></ol></div>`;
        case 'identity': return `<table style="width:100%;border-collapse:collapse;font-size:12px"><tbody><tr><td colspan="2" style="border:1px solid #8896a8;padding:5px 10px"><span style="font-size:9px;color:#526174">NOME DO CANDIDATO</span><br><strong>${field('nome')}</strong></td></tr><tr><td style="width:50%;border:1px solid #8896a8;padding:5px 10px"><span style="font-size:9px;color:#526174">NÚMERO DE INSCRIÇÃO</span><br><strong>${field('inscricao')}</strong></td><td style="border:1px solid #8896a8;padding:5px 10px"><span style="font-size:9px;color:#526174">CPF</span><br><strong>${field('cpf')}</strong></td></tr></tbody></table>`;
        case 'signature': return '<p style="width:76%;border-top:1px solid #172334;text-align:center;padding-top:8px;margin:0 auto;font-size:11px">Assinatura do candidato</p><p style="text-align:center;font-size:9px;margin:8px 0 0">Confira todas as marcações antes de entregar esta folha.</p>';
        default: return '<p>Texto personalizado</p>';
    }
}

export function replaceSheetHtmlTags(html, processo, user, nomeProva, options = {}) {
    let result = [':nome_candidato', ':inscricao_candidato', ':cpf_candidato', ':nome_seletivo', ':nome_cargo', ':nome_prova'].reduce((content, tag) => {
        const candidateTag = [':nome_candidato', ':inscricao_candidato', ':cpf_candidato'].includes(tag);
        if (candidateTag && options.blankCandidate) {
            // Keep a writable blank line instead of collapsing the former placeholder.
            const blank = `<span class="answer-sheet-blank-field" aria-hidden="true" style="display:inline-block;min-width:${tag.length}ch;max-width:100%;min-height:1em">&nbsp;</span>`;
            return content.replaceAll(tag, (match, offset) => content.lastIndexOf('<', offset) > content.lastIndexOf('>', offset) ? '' : blank);
        }
        const value = options.editable && candidateTag && !user ? tag : replaceSheetTags(tag, processo, user, nomeProva);
        return content.split(tag).join(escapeHtml(value));
    }, String(html || ''));
    if (!options.logo) result = result.replace(/<img\b[^>]*src=["']:logo_seletivo["'][^>]*>/gi, '');
    result = result.split(':logo_seletivo').join(escapeHtml(options.logo || ''));
    return result.split(':tipo_folha').join(options.draft ? 'Folha de respostas • Rascunho' : 'Folha de respostas');
}

// Save a complete, reusable model, keeping placeholders instead of preview candidate data.
export function answerSheetSettingsForSave(value) {
    const settings = { ...defaultAnswerSheet, ...value };
    return { ...settings, map: { ...(settings.map || { mode: 'auto' }) }, elements: answerSheetElements(settings).map(element =>
        ['answers', 'barcode'].includes(element.type) ? element : { ...element, content: answerSheetBlockContent(element, settings) }) };
}
